import { jsPDF } from 'jspdf';
import type { RateAgreementLetterFormData, RateAgreementLetterFields, AuditLogEntry } from '../types';
import { resolveSignatureDataUrl } from './signatureUtils';
import { AEL_LOGO_DATA_URI, AEL_LOGO_WIDTH, AEL_LOGO_HEIGHT } from '../../../lib/aelLogo';

/** Returns ordinal suffix for a day number: 1→"1st", 4→"4th", etc. */
function ordinalDay(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Formats an ISO date string ("2026-05-04") as "Monday, May 4th, 2026" */
function formatLetterDate(iso: string): string {
  if (!iso) return '';
  // Parse at noon local time to avoid off-by-one from UTC midnight
  const d = new Date(`${iso}T12:00:00`);
  if (isNaN(d.getTime())) return iso;
  const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
  const month   = d.toLocaleDateString('en-US', { month: 'long' });
  const year    = d.getFullYear();
  return `${weekday}, ${month} ${ordinalDay(d.getDate())}, ${year}`;
}

export async function generateRateAgreementPdf(
  formData: RateAgreementLetterFormData,
  companyName: string,
  signerName: string,
  applicationId: string = 'PENDING',
  signerEmail: string = '',
  auditLog: AuditLogEntry[] = []
): Promise<string> {
  const fields: RateAgreementLetterFields = formData.fields;

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();
  const ml = 22;
  const mr = 22;
  const uw = W - ml - mr;

  let y = 14;

  const font = (style: 'normal' | 'bold' | 'italic', size: number) => {
    pdf.setFont('helvetica', style);
    pdf.setFontSize(size);
  };

  const color = (r: number, g: number, b: number) => pdf.setTextColor(r, g, b);

  const tx = (text: string, x: number, yPos: number, options?: object) => {
    pdf.text(text || '', x, yPos, options as any);
  };

  const wrap = (text: string, x: number, yPos: number, maxWidth: number): number => {
    font('normal', 9.5);
    color(15, 23, 42);
    const lines = pdf.splitTextToSize(text || '', maxWidth);
    pdf.text(lines, x, yPos);
    return yPos + lines.length * (9.5 * 0.3528 * 1.45);
  };

  const hline = (yPos: number) => {
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.25);
    pdf.line(ml, yPos, W - mr, yPos);
  };

  const bullet = (text: string, x: number, yPos: number, maxWidth: number): number => {
    font('normal', 9);
    color(15, 23, 42);
    tx('•', x, yPos);
    const lines = pdf.splitTextToSize(text || '', maxWidth - 5);
    pdf.text(lines, x + 5, yPos);
    return yPos + lines.length * (9 * 0.3528 * 1.45);
  };

  const subbullet = (text: string, x: number, yPos: number, maxWidth: number): number => {
    font('normal', 8.5);
    color(80, 80, 80);
    tx('◦', x + 5, yPos);
    const lines = pdf.splitTextToSize(text || '', maxWidth - 12);
    pdf.text(lines, x + 10, yPos);
    return yPos + lines.length * (8.5 * 0.3528 * 1.45);
  };

  // ── Logo (letterhead, top-left) ───────────────────────────────────────────
  const logoW = 54;
  const logoH = logoW * (AEL_LOGO_HEIGHT / AEL_LOGO_WIDTH); // preserve aspect ratio
  try {
    pdf.addImage(AEL_LOGO_DATA_URI, 'PNG', ml, y, logoW, logoH);
  } catch {
    font('bold', 12);
    color(15, 23, 42);
    tx('ARMELLINI EXPRESS LINES', ml, y + logoH / 2);
  }
  y += logoH + 6;

  hline(y);
  y += 6;

  // ── Date + Email row ──────────────────────────────────────────────────────
  font('bold', 9.5);
  color(15, 23, 42);
  const displayDate = fields.proposalDate || formatLetterDate(fields.letterDate || '') || formatLetterDate(new Date().toISOString().split('T')[0]);
  tx(displayDate, ml, y);
  font('normal', 9.5);
  color(60, 60, 60);
  tx(`Email: ${fields.billingEmail || signerEmail || ''}`, W / 2 + 10, y);
  y += 7;

  hline(y);
  y += 6;

  // ── Bill To & Consignee block ─────────────────────────────────────────────
  const billToType = fields.billToType || 'Bill To/Consignee';
  const deliveryCityState = [fields.destinationCity, fields.destinationState].filter(Boolean).join(', ') || fields.destinationCityState || '';
  const deliveryCityStateZip = [deliveryCityState, fields.destinationZip].filter(Boolean).join(' ') || fields.cityStateZip || '';

  font('bold', 8.5);
  color(3, 105, 161);
  tx(`${billToType}:`, ml, y);
  if (billToType === 'Bill To/Shipper') {
    tx('Consignee:', W / 2 + 10, y);
  }
  y += 4.5;

  font('normal', 9);
  color(60, 60, 60);
  tx(fields.accountNumber || 'New Account', ml, y);
  if (billToType === 'Bill To/Shipper') {
    tx(fields.consigneeAccount || 'New Account', W / 2 + 10, y);
  }
  y += 4.5;

  font('bold', 10);
  color(15, 23, 42);
  tx(companyName, ml, y);
  if (billToType === 'Bill To/Shipper') {
    tx(fields.consigneeName || companyName, W / 2 + 10, y);
  }
  y += 4.5;

  font('normal', 9.5);
  color(30, 30, 30);
  tx(deliveryCityStateZip, ml, y);
  if (billToType === 'Bill To/Shipper') {
    const consCityState = [fields.consigneeCity || fields.destinationCity, fields.consigneeState || fields.destinationState].filter(Boolean).join(', ');
    const consCityStateZip = [consCityState, fields.consigneeZip || fields.destinationZip].filter(Boolean).join(' ');
    tx(consCityStateZip, W / 2 + 10, y);
  }
  y += 8;

  hline(y);
  y += 7;

  // ── Greeting ──────────────────────────────────────────────────────────────
  y = wrap(`Good Day ${signerName},`, ml, y, uw);
  y += 2;
  y = wrap(
    `Thank you for the opportunity to provide ${companyName} with our Service Beyond Expectation™. Below is your rate and detailed information for shipments from Vista, CA to ${deliveryCityState || 'your destination'}.`,
    ml,
    y,
    uw
  );
  y += 6;

  // ── Outbound Day of Service ───────────────────────────────────────────────
  const outboundState = (fields.outboundState || fields.destinationState || 'FL').toUpperCase();
  font('bold', 9.5);
  color(15, 23, 42);
  tx(`${outboundState} Outbound Day of Service`, ml, y);
  y += 5;
  y = bullet(fields.dos || 'As Quoted', ml, y, uw);
  y = bullet(
    `Shipment Date | ${fields.firstShipmentDate || 'Upon Account Activation'}`,
    ml,
    y,
    uw
  );
  y += 5;

  // ── Rate(s) ───────────────────────────────────────────────────────────────
  const minAmount = fields.minimumShipmentAmount
    ? `$${fields.minimumShipmentAmount.replace(/^\$/, '')}`
    : (fields.minimumShipmentDetails || '$125.00');
  const rateAmount = fields.dollarAmount
    ? `$${fields.dollarAmount.replace(/^\$/, '')}`
    : (fields.palletRate || '$0.00');
  const uom = fields.unitOfMeasure || 'Pallet';
  const handlingFee = fields.handlingFee
    ? `$${fields.handlingFee.replace(/^\$/, '')}`
    : '$0.00';

  font('bold', 9.5);
  color(15, 23, 42);
  tx('Rate(s)', ml, y);
  y += 5;
  y = bullet(minAmount, ml, y, uw);
  y = bullet(`${rateAmount}/${uom} + ${handlingFee} /piece handling fee, + fuel surcharge`, ml, y, uw);
  y = subbullet(
    'Lumper fees are additional if required (flowers are delivered where food is & drivers cannot enter).',
    ml,
    y,
    uw
  );
  y += 5;

  // ── Specifics ─────────────────────────────────────────────────────────────
  font('bold', 9.5);
  color(15, 23, 42);
  tx('Specifics', ml, y);
  y += 5;

  const specifics = [
    `Minimum per shipment: ${minAmount}`,
    'Above rate is subject to holiday increase as stated on our holiday shipping schedules when applicable.',
    'To secure these rates, your first shipment must occur within 30 days of signing the rate agreement.',
    `Terms ${fields.paymentTerms || 'Net 15 Days'}. Credit Card payments are subject to a convenience fee.`,
    'All shipments and transportation services are subject to the terms and conditions of the Armellini Contract of Carriage.',
    'Any discrepancies must be reported within 24 hours of delivery.',
  ];
  for (const item of specifics) {
    y = bullet(item, ml, y, uw);
  }
  y += 5;

  // ── Benefits ──────────────────────────────────────────────────────────────
  if (y > H - 80) {
    pdf.addPage();
    y = 20;
  }

  font('bold', 9.5);
  color(15, 23, 42);
  tx('Armellini Express Lines Benefits', ml, y);
  y += 5;

  const benefits = [
    'All trailers travel at 36 degrees fahrenheit.',
    'Satellite tracking and online ETA visibility.',
    'Bar Code scanning of all boxes/pallets received and loaded.',
    'Refrigerated docks.',
    'Latest model equipment for consistent quality service.',
    'Air-ride spacesaver trailers with ThermoKing refrigeration units that provide a higher volume of airflow for better temperature control. Built in temperature-recording device in each trailer for quality assurance and monitoring of all loads.',
    'Team drivers for faster deliveries and better service.',
    '24/7 Customer support',
  ];
  for (const b of benefits) {
    y = bullet(b, ml, y, uw);
  }
  y += 6;

  if (y > H - 70) {
    pdf.addPage();
    y = 20;
  }

  // ── Sign-and-return paragraph ─────────────────────────────────────────────
  font('normal', 9.5);
  color(15, 23, 42);
  y = wrap(
    `Please sign and return to ${fields.senderEmail || 'ccenter@armellini.com'} at your earliest convenience. We pride ourselves in providing reliable, consistent service to you and your customers. We look forward to assisting you with your freight needs. Thank you for choosing us as your transportation partner!`,
    ml,
    y,
    uw
  );
  y += 6;

  font('normal', 9.5);
  color(15, 23, 42);
  tx('Best Regards,', ml, y);
  y += 5;
  font('bold', 9.5);
  tx(fields.senderName || 'Armellini Customer Center', ml, y);
  y += 4;
  font('normal', 9.5);
  tx('Armellini Express Lines', ml, y);
  y += 10;

  hline(y);
  y += 8;

  // ── Signature block ───────────────────────────────────────────────────────
  font('bold', 10);
  color(15, 23, 42);
  tx('DATE:', ml, y);
  font('normal', 10);
  tx(formData.signedDate || '', ml + 16, y);
  y += 9;

  font('bold', 10);
  tx('SIGNED:', ml, y);
  const sigDataUrl = resolveSignatureDataUrl(formData.signature, 88, 18);
  if (sigDataUrl) {
    try {
      pdf.addImage(sigDataUrl, 'PNG', ml + 20, y - 7, 88, 18);
    } catch {
      pdf.setDrawColor(55, 65, 81);
      pdf.setLineWidth(0.3);
      pdf.line(ml + 20, y + 3, ml + 110, y + 3);
    }
  } else {
    pdf.setDrawColor(55, 65, 81);
    pdf.setLineWidth(0.3);
    pdf.line(ml + 20, y + 3, ml + 110, y + 3);
  }
  y += 12;

  font('bold', 10);
  tx('PRINT:', ml, y);
  font('normal', 10);
  color(15, 23, 42);
  tx(formData.printName || '', ml + 20, y);
  pdf.setDrawColor(55, 65, 81);
  pdf.setLineWidth(0.3);
  pdf.line(ml + 20, y + 3, ml + 110, y + 3);
  y += 10;

  // ── Digital signature attestation ─────────────────────────────────────────
  const sigId = applicationId && applicationId !== 'PENDING'
    ? applicationId.toUpperCase()
    : Array.from({ length: 8 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase() + '-' +
      Array.from({ length: 4 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase() + '-' +
      Array.from({ length: 4 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase() + '-' +
      Array.from({ length: 4 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase() + '-' +
      Array.from({ length: 12 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase();

  const sigDateTime = new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
    timeZone: 'America/New_York',
  }).format(new Date());

  pdf.setDrawColor(200, 200, 200);
  pdf.setFillColor(248, 249, 250);
  pdf.setLineWidth(0.3);
  pdf.roundedRect(ml, y, uw, 24, 2, 2, 'FD');

  font('normal', 7.5);
  color(80, 80, 80);
  tx(`ID: ${sigId}`, ml + 4, y + 5);
  tx(`Digitally signed by: ${signerEmail || signerName}`, ml + 4, y + 9.5);
  tx('Contract of Carriage: Reviewed & Accepted', ml + 4, y + 14);
  tx(`${sigDateTime} EDT`, ml + 4, y + 18.5);
  y += 28;

  // ── Audit Log (page 2) ────────────────────────────────────────────────────
  if (auditLog.length > 0) {
    pdf.addPage();
    y = 14;

    const mlA = 25.4;
    const mrA = 25.4;
    const uwA = W - mlA - mrA;

    // Logo
    try {
      const logoW = 36;
      const logoH = logoW * (AEL_LOGO_HEIGHT / AEL_LOGO_WIDTH);
      pdf.addImage(AEL_LOGO_DATA_URI, 'PNG', mlA, y, logoW, logoH);
    } catch {
      font('bold', 10);
      color(15, 23, 42);
      tx('ARMELLINI LOGISTICS', mlA, y + 6);
    }

    font('normal', 16);
    pdf.setTextColor(100);
    tx('Audit Log', W - mrA - pdf.getTextWidth('Audit Log') - 10, y + 8);
    pdf.setTextColor(0);

    y += 18;
    font('bold', 9);
    color(15, 23, 42);
    tx(`Envelope ID: ${applicationId}`, mlA, y);

    y += 4;
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.25);
    pdf.line(mlA, y, W - mrA, y);
    y += 6;

    // Document header
    const docName = `Rate Agreement Letter - ${companyName}`;
    font('normal', 8);
    color(15, 23, 42);
    tx(docName, mlA, y);

    // Hash box (right side)
    const boxW = 85;
    const boxX = W - mrA - boxW;
    pdf.setDrawColor(200);
    pdf.setFillColor(248, 249, 250);
    pdf.setLineWidth(0.5);
    pdf.rect(boxX, y - 4, boxW, 26, 'FD');

    const hashgen = () => Array.from({ length: 64 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    font('bold', 8);
    tx('Original SHA256:', boxX + 4, y);
    font('normal', 6);
    const hashLines1 = pdf.splitTextToSize(hashgen(), boxW - 8);
    pdf.text(hashLines1, boxX + 4, y + 4);
    font('bold', 8);
    tx('Result SHA256:', boxX + 4, y + 10);
    font('normal', 6);
    const hashLines2 = pdf.splitTextToSize(hashgen(), boxW - 8);
    pdf.text(hashLines2, boxX + 4, y + 14);
    font('bold', 8);
    tx('Generated at:', boxX + 4, y + 19);
    font('normal', 7);
    const generatedDate = new Intl.DateTimeFormat('en-US', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: 'America/New_York',
    }).format(new Date());
    tx(generatedDate, boxX + 4 + pdf.getTextWidth('Generated at: ') + 1, y + 19);

    y += 10;
    font('bold', 9);
    color(15, 23, 42);
    tx(signerEmail || signerName, mlA, y);
    y += 6;
    font('normal', 8);
    tx('Email verification: Verified', mlA, y);
    y += 5;

    const lastEvent = auditLog[auditLog.length - 1];
    if (lastEvent?.ip) {
      tx(`IP: ${lastEvent.ip}`, mlA, y);
      y += 5;
    }
    if (lastEvent?.userAgent) {
      const uaLines = pdf.splitTextToSize(`User agent: ${lastEvent.userAgent}`, uwA - boxW - 10);
      pdf.text(uaLines, mlA, y);
      y += uaLines.length * 3.5 + 1.5;
    }

    y += 8;

    // Signature on audit page
    font('normal', 6);
    pdf.setTextColor(150);
    tx('SIGNATURE', mlA, y);
    pdf.setTextColor(0);
    y += 2;
    const auditSigUrl = resolveSignatureDataUrl(formData.signature, 72, 18);
    if (auditSigUrl) {
      pdf.addImage(auditSigUrl, 'PNG', mlA, y, 72, 18);
    }
    y += 24;

    font('normal', 6);
    pdf.setTextColor(150);
    tx('DATE SIGNED', mlA, y);
    pdf.setTextColor(0);
    y += 4;
    font('normal', 8);
    color(15, 23, 42);
    tx(formData.signedDate || '', mlA, y);
    y += 10;

    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.25);
    pdf.line(mlA, y, W - mrA, y);
    y += 8;

    // Event log
    font('bold', 12);
    pdf.setTextColor(100);
    tx('Event Log', mlA, y);
    pdf.setTextColor(0);
    y += 8;

    for (const evt of auditLog) {
      if (y > 280) {
        pdf.addPage();
        y = 14;
      }
      let ts = new Date();
      if (evt.timestamp) {
        if (typeof (evt.timestamp as any).toDate === 'function') {
          ts = (evt.timestamp as any).toDate();
        } else {
          ts = new Date(evt.timestamp as any);
        }
      }
      const evtTime = new Intl.DateTimeFormat('en-US', {
        dateStyle: 'long',
        timeStyle: 'short',
        timeZone: 'America/New_York',
      }).format(ts);
      font('normal', 8);
      color(15, 23, 42);
      tx(evtTime, mlA, y);
      font('bold', 8);
      tx(evt.action, mlA + 55, y);
      font('normal', 8);
      const detailLines = pdf.splitTextToSize(evt.details || '', uwA - 110);
      pdf.text(detailLines, mlA + 100, y);
      y += Math.max(detailLines.length, 1) * 5 + 1;
    }

    y += 6;
    font('normal', 6);
    pdf.setTextColor(150);
    tx(
      `Document ID: ${Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('').toUpperCase()}`,
      mlA,
      y
    );
    pdf.setTextColor(0);

    // Footer on audit page
    font('normal', 7.5);
    color(120, 120, 120);
    tx(
      'P.O. BOX 520646 Miami, Florida 33152  —  Phone: (786) 547-9894  —  ccenter@armellini.com',
      W / 2,
      H - 12,
      { align: 'center' } as any
    );
  }

  // Return base64 (strip the data URI prefix)
  const dataUri = pdf.output('datauristring');
  return dataUri.split(',')[1];
}
